import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Check, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fileTypes = [
  {
    id: "coa",
    label: "Chart of Accounts",
    description: "Upload COA.xml with groups and ledgers",
    endpoint: "/upload/coa",
    accept: ".xml",
  },
  {
    id: "trial-balance",
    label: "Trial Balance",
    description: "Upload TrialBal.xml with account balances",
    endpoint: "/upload/trial-balance",
    accept: ".xml",
  },
  {
    id: "daybook",
    label: "Daybook / Vouchers",
    description: "Upload one OR multiple daybook XMLs (e.g. April + May + June)",
    endpoint: "/upload/daybook",
    accept: ".xml",
    multiple: true,
  },
  {
    id: "voucher-master",
    label: "Voucher Master",
    description: "Upload voucher type definitions XML",
    endpoint: "/upload/voucher-master",
    accept: ".xml",
  },
];

export default function FileUpload({ onUploadSuccess, companyContext }) {
  const [uploadStatus, setUploadStatus] = useState({});
  const [dragging, setDragging] = useState(null);
  const fileInputRefs = useRef({});
  const [branchForUpload, setBranchForUpload] = useState("main");
  // Period for Trial Balance uploads (YYYY-MM). Defaults to current month.
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [tbPeriodMonth, setTbPeriodMonth] = useState(defaultMonth);

  const currentCompany = (companyContext?.companies || []).find(c => c.company_id === companyContext?.companyId);
  const hasBranches = currentCompany?.has_branches && currentCompany?.branches?.length > 0;

  const handleDragOver = (e, typeId) => {
    e.preventDefault();
    setDragging(typeId);
  };

  const handleDragLeave = () => {
    setDragging(null);
  };

  const handleDrop = async (e, fileType) => {
    e.preventDefault();
    setDragging(null);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      if (fileType.multiple) await uploadFiles(files, fileType);
      else await uploadFile(files[0], fileType);
    }
  };

  const handleFileSelect = async (e, fileType) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      if (fileType.multiple) await uploadFiles(files, fileType);
      else await uploadFile(files[0], fileType);
    }
    // reset input so the same file(s) can be picked again
    e.target.value = "";
  };

  /**
   * Upload an array of files sequentially. Used for daybook so smart period-merge
   * applies per file (each file's date range only touches its own vouchers).
   * Aggregates progress + final totals into a single status entry.
   */
  const uploadFiles = async (files, fileType) => {
    let totalRecords = 0;
    let totalReplaced = 0;
    const failures = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadStatus((prev) => ({
        ...prev,
        [fileType.id]: {
          status: "uploading",
          message: `Uploading ${i + 1}/${files.length}: ${file.name}…`,
        },
      }));
      try {
        const companyId = companyContext?.companyId || "default";
        const branchId = companyContext?.branchId || branchForUpload || "main";
        const url = `${API}${fileType.endpoint}?company_id=${companyId}&branch_id=${branchId}`;
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        totalRecords += response.data.records_processed || 0;
        totalReplaced += response.data.replaced || 0;
      } catch (error) {
        failures.push({ name: file.name, error: error.response?.data?.detail || "Upload failed" });
      }
    }

    if (failures.length === 0) {
      const msg =
        `${files.length} file${files.length === 1 ? "" : "s"} processed — ` +
        `${totalRecords} record${totalRecords === 1 ? "" : "s"} total` +
        (totalReplaced ? `, ${totalReplaced} replaced` : "");
      setUploadStatus((prev) => ({
        ...prev,
        [fileType.id]: { status: "success", message: msg, records: totalRecords },
      }));
      toast.success(`${fileType.label}: ${msg}`);
      onUploadSuccess?.();
    } else {
      const msg =
        `${files.length - failures.length}/${files.length} succeeded; ` +
        failures.map((f) => `${f.name}: ${f.error}`).join("; ");
      setUploadStatus((prev) => ({
        ...prev,
        [fileType.id]: { status: "error", message: msg },
      }));
      toast.error(`${fileType.label}: ${failures.length} of ${files.length} file(s) failed`);
      if (files.length - failures.length > 0) onUploadSuccess?.();
    }
  };

  const uploadFile = async (file, fileType) => {
    setUploadStatus((prev) => ({
      ...prev,
      [fileType.id]: { status: "uploading", message: "Uploading..." },
    }));

    const formData = new FormData();
    formData.append("file", file);

    try {
      const companyId = companyContext?.companyId || "default";
      const branchId = companyContext?.branchId || branchForUpload || "main";
      let url = `${API}${fileType.endpoint}?company_id=${companyId}&branch_id=${branchId}`;
      if (fileType.id === "trial-balance") {
        if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(tbPeriodMonth)) {
          setUploadStatus((prev) => ({
            ...prev,
            [fileType.id]: { status: "error", message: "Pick a valid month (YYYY-MM) for this TB" },
          }));
          toast.error("Pick a valid month for the Trial Balance before uploading");
          return;
        }
        url += `&period_month=${encodeURIComponent(tbPeriodMonth)}`;
      }
      const response = await axios.post(
        url,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      setUploadStatus((prev) => ({
        ...prev,
        [fileType.id]: {
          status: "success",
          message: response.data.message,
          records: response.data.records_processed,
        },
      }));

      toast.success(`${fileType.label} uploaded successfully!`);
      onUploadSuccess?.();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Upload failed";
      setUploadStatus((prev) => ({
        ...prev,
        [fileType.id]: { status: "error", message: errorMsg },
      }));
      toast.error(errorMsg);
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return null;
    switch (status.status) {
      case "uploading":
        return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      case "success":
        return <Check className="w-5 h-5 text-green-600" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  return (
    <div data-testid="file-upload">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="upload-title">
          Upload Files
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload your Tally XML exports to generate MIS reports
          {currentCompany && <span className="font-medium"> for {currentCompany.name}</span>}
        </p>
      </div>

      {/* Branch selector for upload */}
      {hasBranches && (
        <div className="mb-4 flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded">
          <span className="text-sm text-blue-800 font-medium">Upload to branch:</span>
          <select value={branchForUpload} onChange={(e) => setBranchForUpload(e.target.value)}
            className="text-sm border border-blue-200 rounded px-2 py-1 bg-white"
            data-testid="upload-branch-select">
            <option value="main">Main</option>
            {currentCompany.branches.map((b) => (
              <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Upload Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {fileTypes.map((fileType) => {
          const status = uploadStatus[fileType.id];
          return (
            <Card
              key={fileType.id}
              className="border border-slate-200 shadow-none"
              data-testid={`upload-card-${fileType.id}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  {fileType.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-4">{fileType.description}</p>

                {/* Month picker — only for Trial Balance */}
                {fileType.id === "trial-balance" && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
                    <label htmlFor="tb-period-month"
                      className="block text-[11px] font-bold uppercase tracking-wide text-amber-900 mb-1">
                      As of month (YYYY-MM) *
                    </label>
                    <input
                      id="tb-period-month"
                      type="month"
                      value={tbPeriodMonth}
                      onChange={(e) => setTbPeriodMonth(e.target.value)}
                      className="w-full text-sm border border-amber-300 rounded px-2 py-1 bg-white"
                      data-testid="tb-period-month-input"
                    />
                    <p className="text-[10px] text-amber-700 mt-1">
                      Each month&apos;s TB is stored separately. Re-uploading the same month replaces only that snapshot.
                    </p>
                  </div>
                )}

                {/* Drop Zone */}
                <div
                  className={`upload-zone ${dragging === fileType.id ? "dragging" : ""}`}
                  onDragOver={(e) => handleDragOver(e, fileType.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, fileType)}
                  onClick={() => fileInputRefs.current[fileType.id]?.click()}
                  data-testid={`drop-zone-${fileType.id}`}
                >
                  <input
                    type="file"
                    ref={(el) => (fileInputRefs.current[fileType.id] = el)}
                    accept={fileType.accept}
                    multiple={!!fileType.multiple}
                    onChange={(e) => handleFileSelect(e, fileType)}
                    className="hidden"
                    data-testid={`file-input-${fileType.id}`}
                  />
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">
                    {fileType.multiple
                      ? "Drop file(s) here or click to browse — multi-select supported"
                      : "Drop file here or click to browse"}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">XML files only</p>
                </div>

                {/* Status */}
                {status && (
                  <div
                    className={`mt-4 p-3 rounded-sm flex items-center gap-2 ${
                      status.status === "success"
                        ? "bg-green-50 text-green-800"
                        : status.status === "error"
                        ? "bg-red-50 text-red-800"
                        : "bg-blue-50 text-blue-800"
                    }`}
                    data-testid={`upload-status-${fileType.id}`}
                  >
                    {getStatusIcon(status)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{status.message}</p>
                      {status.records !== undefined && (
                        <p className="text-xs opacity-75">
                          {status.records} records processed
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Instructions */}
      <Card className="mt-6 border border-slate-200 shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">
            Upload Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
            <li>
              <strong>Step 1:</strong> Export COA (Chart of Accounts) from Tally and upload it first
            </li>
            <li>
              <strong>Step 2:</strong> Export Trial Balance from Tally and upload <em>— pick the period (YYYY-MM) before uploading; each month&apos;s TB is stored separately, so you can upload April + May + June and reports will pick the latest snapshot automatically.</em>
            </li>
            <li>
              <strong>Step 3:</strong> Export Daybook/Vouchers XML and upload <em>— you can upload multiple Daybook files for different date ranges. Each upload only replaces vouchers within its own date range, so April&apos;s data stays intact when you later add May.</em>
            </li>
            <li>
              <strong>Step 4:</strong> Navigate to reports to view your financial statements
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
